import React, { useEffect, useState } from 'react';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Popover, SxProps, Theme, Typography } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';

import { useLanguage } from '../../hooks';

const customStyles: Record<string, SxProps<Theme>> = {
  button: {
    cursor: 'pointer',
    marginLeft: '10%',
    textTransform: 'none',

    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  Popover: {
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '15px',
  },
  PopoverContent: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: '3px',
    fontSize: '14px',
    padding: '10px 20px',
    width: '200px',
    height: '200px',
  },
  PopOverPaper: {
    borderRadius: '8px',
  },
  checkBoxContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  checkBox: {
    paddingLeft: '0px',
    radius: '4px',
    color: '#D1D4D9',
    '& .MuiCheckbox-root': {
      borderRadius: '20px',
    },
    '&.Mui-checked': {
      color: '#3E8FFA',
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  limitWarning: {
    marginTop: '7%',
    fontSize: '12px',
    color: '#4760F6',
  },
  TextField: {
    width: '120px',
  },
  TextFieldinput: {
    padding: '5px',
  },
  TextFieldInput: {
    fontSize: '12px',
  },
};

export const LimitSubformsOption = ({
  paying,
  styles,
  questionId,
  max_subforms,
  min_subforms,
  onSubformsLimitChange,
}: {
  paying: boolean;
  styles: any;
  questionId: string;
  max_subforms: string | undefined;
  min_subforms: string | undefined;
  onSubformsLimitChange: (id: string, value: string, type: string) => void;
}) => {
  const checkDefaultMaxSubforms = paying ? '' : '5';

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const [mounted, setMounted] = useState(false);

  const [maxCheckBoxValue, setMaxCheckBoxValue] = useState<string>(
    max_subforms ? max_subforms : checkDefaultMaxSubforms,
  );
  const [minCheckBoxValue, setMinCheckBoxValue] = useState<string>(min_subforms ?? '');

  const [isWarningOpen, setIsWarningOpen] = useState(false);

  useEffect(() => {
    // useEffect for min checkbox value:
    if (mounted) {
      onSubformsLimitChange(questionId, minCheckBoxValue, 'min');
    }
    setMounted(true);
  }, [minCheckBoxValue, onSubformsLimitChange, questionId]);

  useEffect(() => {
    // useEffect for max checkbox value:
    if (mounted) {
      onSubformsLimitChange(questionId, maxCheckBoxValue, 'max');
    }
    setMounted(true);
  }, [maxCheckBoxValue, onSubformsLimitChange, questionId]);

  const onMinTextHandler = (questionId: string, value: string) => {
    //@ts-ignore
    if (isNaN(value) || value === '') return;
    if (parseInt(value, 10) < 1) return;
    if (!paying && parseInt(value, 10) > 5) {
      return setIsWarningOpen(true);
    }
    if (parseInt(value, 10) > parseInt(maxCheckBoxValue, 10)) return;
    if (parseInt(value, 10) > 100) return;
    setMinCheckBoxValue(value);
    // onSubformsLimitChange(questionId, value, 'min');
  };
  const onMaxTextHandler = (questionId: string, value: string) => {
    //@ts-ignore
    if (isNaN(value) || value === '') return;
    if (parseInt(value, 10) < 1) return;
    if (parseInt(value, 10) < parseInt(minCheckBoxValue, 10)) return;
    if (!paying && parseInt(value, 10) > 5) {
      return setIsWarningOpen(true);
    }
    if (parseInt(value, 10) > 100) return;
    setMaxCheckBoxValue(value);
    // onSubformsLimitChange(questionId, value, 'max');
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMinCheckBoxChange = () => {
    if (minCheckBoxValue !== '') setMinCheckBoxValue('');
    if (minCheckBoxValue === '') setMinCheckBoxValue('1');
  };
  const handleMaxCheckBoxChange = () => {
    if (!paying) return;
    if (maxCheckBoxValue !== '') setMaxCheckBoxValue('');
    if (maxCheckBoxValue === '') {
      if (minCheckBoxValue !== '') {
        setMaxCheckBoxValue(minCheckBoxValue);
      } else {
        setMaxCheckBoxValue('1');
      }
    }
  };

  const lang = useLanguage();
  const popoverOpen = Boolean(anchorEl);

  return (
    <>
      <Box>
        <Button
          onClick={handleClick}
          sx={customStyles.button}
          variant={'text'}
          disableRipple
          disableTouchRipple
          disableFocusRipple
        >
          <Typography sx={{ ...styles.labels, whiteSpace: 'nowrap' }}>
            {lang.form_builder.subformLimit}
          </Typography>
          {popoverOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Button>
        <Popover
          id={popoverOpen ? 'simple-popover' : undefined}
          sx={customStyles.Popover}
          open={popoverOpen}
          onClose={handleClose}
          anchorEl={anchorEl}
          PaperProps={{
            sx: customStyles.PopOverPaper,
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
        >
          <Box sx={customStyles.PopoverContent}>
            <Box sx={customStyles.checkBoxContainer}>
              <Checkbox
                disableTouchRipple
                size={'small'}
                color={'default'}
                checked={!!minCheckBoxValue}
                onChange={handleMinCheckBoxChange}
                sx={customStyles.checkBox}
              />
              {lang.form_builder.min}
            </Box>
            <TextField
              value={minCheckBoxValue}
              type={'number'}
              onChange={(e) => onMinTextHandler(questionId, e.target.value)}
              disabled={!minCheckBoxValue}
              size={'small'}
              variant={'outlined'}
              sx={customStyles.TextField}
              inputProps={{
                sx: customStyles.TextFieldinput,
              }}
              InputProps={{ sx: customStyles.TextFieldInput }}
            />
            <Box sx={customStyles.checkBoxContainer}>
              <Checkbox
                disableTouchRipple
                size={'small'}
                color={'default'}
                checked={!!maxCheckBoxValue}
                onChange={handleMaxCheckBoxChange}
                sx={customStyles.checkBox}
              />
              {lang.form_builder.max}
            </Box>
            <TextField
              value={maxCheckBoxValue}
              type={'number'}
              onChange={(e) => onMaxTextHandler(questionId, e.target.value)}
              disabled={!maxCheckBoxValue}
              size={'small'}
              variant={'outlined'}
              sx={customStyles.TextField}
              inputProps={{
                sx: customStyles.TextFieldinput,
              }}
              InputProps={{ sx: customStyles.TextFieldInput }}
            />
            <Typography sx={customStyles.limitWarning}>
              {isWarningOpen ? lang.form_builder.subformWarning : null}
            </Typography>
          </Box>
        </Popover>
      </Box>
    </>
  );
};
